<template>
  <div class="orders-view">
    <!-- Header -->
    <div class="view-header">
      <h2><i class="fas fa-clipboard-list me-2"></i>Auftragsverwaltung</h2>
      <div class="header-actions">
        <button
          v-if="isOwner"
          class="archived-toggle-btn"
          :class="{ 'is-active': showArchived }"
          @click="toggleArchived"
        >
          <i class="fas fa-archive"></i>
          <span>Archivierte</span>
        </button>
        <div v-if="isOwner" class="view-toggle">
          <button
            class="view-toggle-btn"
            :class="{ 'is-active': viewMode === 'list' }"
            @click="viewMode = 'list'"
            title="Listenansicht"
          >
            <i class="fas fa-list"></i>
          </button>
          <button
            class="view-toggle-btn"
            :class="{ 'is-active': viewMode === 'board' }"
            @click="viewMode = 'board'"
            title="Board-Ansicht"
          >
            <i class="fas fa-columns"></i>
          </button>
        </div>
        <button class="import-btn" @click="showJdfUpload = true">
          <i class="fas fa-file-import"></i>
          <span>Import</span>
        </button>
      </div>
    </div>

    <!-- Kopf-Reiter (Chef 10.06.): Auftrags-Umfang wählen. -->
    <div class="status-tabs mb-2">
      <button
        v-for="t in statusTabs"
        :key="t.value"
        class="status-tab"
        :class="{ 'is-active': activeTab === t.value }"
        @click="selectTab(t.value)"
      >{{ t.label }}</button>
    </div>

    <!-- Filter bar -->
    <div class="filter-bar mb-3">
      <div class="filter-row">

        <filter-dropdown
            v-model="filters.status"
            :options="statusOptions.map(s => ({ value: s.value, label: s.text, color: s.color }))"
            label="Status"
            placeholder="Alle Status"
            :show-colors="true"
            @update:modelValue="applyFilters"
        />

        <filter-dropdown
            v-model="filters.employee_id"
            :options="employeeList.map(e => ({ value: String(e.id), label: e.name, icon_url: e.icon_url, id: e.id }))"
            label="Mitarbeiter"
            placeholder="Alle Mitarbeiter"
            :show-avatars="true"
            @update:modelValue="applyFilters"
        />

        <filter-dropdown
            v-model="filters.priority"
            :options="priorityOptions"
            label="Priorität"
            placeholder="Alle Prioritäten"
            @update:modelValue="applyFilters"
        />

        <filter-dropdown
            v-model="filters.deliveryMethod"
            :options="deliveryMethods.map(m => ({ value: String(m.value), label: m.text }))"
            label="Versandart"
            placeholder="Alle Versandarten"
            @update:modelValue="applyFilters"
        />

        <filter-dropdown
            v-model="filters.site"
            :options="[{ value: 'pk', label: 'Pritzwalk' }, { value: 'sb', label: 'Tastomat' }]"
            label="Ort"
            placeholder="Alle Orte"
            @update:modelValue="applyFilters"
        />

        <div class="date-range">
          <label class="date-label">Lieferdatum</label>
          <div class="date-inputs">
            <input type="date" class="date-input" v-model="filters.dateFrom" @change="applyFilters" placeholder="Von">
            <span class="date-sep">–</span>
            <input type="date" class="date-input" v-model="filters.dateTo"   @change="applyFilters" placeholder="Bis">
          </div>
        </div>

        <button
            v-if="activeFilterCount > 0"
            class="clear-filters-btn"
            @click="resetFilters"
        >
          <i class="fas fa-times" />
          <span>Zurücksetzen</span>
          <span class="filter-count">{{ activeFilterCount }}</span>
        </button>

      </div>
    </div>

    <!-- JDF Upload Modal -->
    <jdf-upload-component v-if="showJdfUpload" @close="handleJdfModalClose" />

    <!-- Customer Details Modal -->
    <div v-if="showCustomerModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Kundendetails</h5>
            <button type="button" class="btn-close" @click="closeCustomerModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingCustomer" class="text-center">
              <div class="spinner-border" role="status"><span class="visually-hidden">Laden...</span></div>
            </div>
            <div v-else-if="selectedCustomer">
              <h6>{{ selectedCustomer.name }}</h6>
              <p><strong>Adresse:</strong> {{ selectedCustomer.address }}</p>
              <p><strong>Telefon:</strong> {{ selectedCustomer.phone }}</p>
              <p><strong>Email:</strong> {{ selectedCustomer.email }}</p>
              <h6 class="mt-4">Kontakte</h6>
              <div v-if="loadingContacts" class="spinner-border spinner-border-sm" role="status"></div>
              <div v-else-if="customerContacts.length > 0">
                <div v-for="contact in customerContacts" :key="contact.id" class="mb-2">
                  <strong>{{ contact.name }}</strong> - {{ contact.email }}
                </div>
              </div>
              <p v-else class="text-muted">Keine Kontakte gefunden</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showCustomerModal" class="modal-backdrop fade show"></div>

    <!-- TODO(#42): old-deadlines-bar disabled — re-enable once delivery dates are
         wired to real data (currently noisy from demo/randomized dates).
    <div v-if="oldDeadlineOrders.length" class="old-deadlines-bar">
      <span class="old-title">
        <i class="fas fa-clock"></i>
        Alte Termine ({{ oldDeadlineOrders.length }})
      </span>
      <span
        v-for="o in oldDeadlineOrders"
        :key="`old-${o.id}`"
        class="old-chip"
        @click="viewOrder(o.id)"
      >
        <span class="pk-sb-dot" :class="o.pk_sb"></span>
        <span class="old-num">{{ o.order_number }}</span>
        <span class="old-date">{{ formatDate(o.delivery_date) }}</span>
      </span>
    </div>
    -->

    <!-- Board view -->
    <order-board-view
      v-if="viewMode === 'board'"
      :orders="orders"
      :employee-list="employeeList"
      :show-archived="showArchived"
    />

    <!-- Table -->
    <data-table
      v-if="viewMode === 'list'"
        title="Aufträge"
        :endpoint="'orders'"
        :columns="columns"
        :data="displayOrders"
        :row-class="rowClass"
        :cell-class="cellClass"
        :loading="loading"
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-count="totalCount"
        :initial-search="filters.search"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="changePage"
        @per-page-change="changePerPage"
    >
      <template v-slot:order_number="{ item }">
        <div class="order-number-cell">
          <copyable :text="item.order_number">
            <strong>{{ item.order_number }}</strong>
          </copyable>
          <order-flags :order="item" />
        </div>
        <small class="text-muted">{{ item.invoice_number || '-' }}</small>
      </template>

      <template v-slot:color="{ item }">
        <farbigkeit-square :value="item.farbigkeit || item.color" />
      </template>

      <template v-slot:priority="{ item }">
        <span class="badge priority-badge" :class="getPriorityClass(item.priority)">
          {{ getPriorityText(item.priority) }}
        </span>
      </template>

      <template v-slot:standort="{ item }">
        <button
            type="button"
            class="standort-filter-btn"
            :class="{ active: filters.site && filters.site === item.pk_sb }"
            :title="item.pk_sb === 'sb' ? 'Nur Tastomat anzeigen' : 'Nur Pritzwalk anzeigen'"
            @click="filterBySite(item.pk_sb)"
        >
          <standort-logo :order-number="item.order_number" :pk-sb="item.pk_sb" />
        </button>
      </template>

      <template v-slot:status="{ item }">
        {{ getStatusText(item.status) }}
      </template>

      <template v-slot:customer_name="{ item }">
        <router-link :to="`/kunden/${item.customer_id}`" class="text-decoration-none">
          {{ item.customer_name }}
        </router-link>
      </template>

      <template v-slot:employee_id="{ item }">
        <div v-if="item.employee_id" class="emp-avatar-cell">
          <router-link :to="`/mitarbeiter/${item.employee_id}`" class="emp-avatar-link">
            <user-avatar
              :src="item.employee_icon_url || getEmployee(item.employee_id)?.icon_url"
              :seed="item.employee_id"
              :name="item.employee_name || '?'"
              :size="28"
            />
          </router-link>
          <div class="emp-tooltip">
            <user-avatar
              :src="item.employee_icon_url || getEmployee(item.employee_id)?.icon_url"
              :seed="item.employee_id"
              :name="item.employee_name || '?'"
              :size="32"
            />
            <span>{{ item.employee_name || 'Unbekannt' }}</span>
          </div>
        </div>
        <span v-else class="text-muted">–</span>
      </template>

      <template v-slot:print_run="{ item }">{{ formatNumber(item.print_run) }}</template>
      <!-- Boss-Mail 2: Lieferdatum TT.MM.JJ, Uhrzeit darunter -->
      <template v-slot:delivery_date="{ item }">
        <div class="stacked-cell">
          <span>{{ formatDateDE(item.delivery_date) }}</span>
          <small v-if="formatTime(item.delivery_date)" class="sub-line">{{ formatTime(item.delivery_date) }}</small>
        </div>
      </template>
      <template v-slot:print_date="{ item }">{{ formatDateDE(item.print_date) }}</template>
      <!-- Boss-Mail 2: Format "12,0 x 34,0" in cm, ohne Einheit -->
      <template v-slot:format="{ item }">{{ formatSheetFormat(item.format) }}</template>
      <!-- Boss-Mail 2: Druckformat mit Druckmaschine darunter -->
      <template v-slot:print_format="{ item }">
        <div class="stacked-cell">
          <span>{{ item.print_format || '–' }}</span>
          <small v-if="item.maschine_name" class="sub-line">{{ item.maschine_name }}</small>
        </div>
      </template>
      <template v-slot:delivery_method="{ item }">
        <button
            v-if="item.delivery_method !== null && item.delivery_method !== undefined && item.delivery_method !== ''"
            type="button"
            class="cell-filter-btn"
            :class="{ active: filters.deliveryMethod !== '' && filters.deliveryMethod === String(item.delivery_method) }"
            :title="`Nur ${formatDeliveryMethod(item.delivery_method)} anzeigen`"
            @click="filterByDeliveryMethod(item.delivery_method)"
        >{{ formatDeliveryMethod(item.delivery_method) }}</button>
        <span v-else class="text-muted">–</span>
      </template>

      <template v-slot:material_status="{ item }">
        <span class="badge" :class="getMaterialStatusClass(item.material_status)">
          {{ getMaterialStatusText(item.material_status) }}
        </span>
      </template>

      <template v-slot:actions="{ item }">
        <div class="btn-group btn-group-sm">
          <button class="btn btn-outline-primary"   @click="viewOrder(item.id)"   title="Anzeigen">
            <font-awesome-icon icon="eye" />
          </button>
          <button class="btn btn-outline-secondary" @click="editOrder(item.id)"   title="Bearbeiten">
            <font-awesome-icon icon="edit" />
          </button>
          <button class="btn btn-outline-danger"    @click="deleteOrder(item.id)" title="Löschen">
            <font-awesome-icon icon="trash" />
          </button>
        </div>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DataTable from '@/components/DataTable.vue'
import Copyable from '@/components/Copyable.vue'
import FilterDropdown from '@/components/FilterDropdown.vue'
import JdfUploadComponent from '@/components/FileUpload.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import OrderBoardView from '@/components/OrderBoardView.vue'
import OrderFlags from '@/components/OrderFlags.vue'
import FarbigkeitSquare from '@/components/FarbigkeitSquare.vue'
import StandortLogo from '@/components/StandortLogo.vue'
import { OrderService, OrderFormatter, ORDER_CONSTANTS } from '@/services/orderService.js'
import { UrlFilters } from '@/composables/urlFilters.js'
import '@/styles/orderStyles.scss'

const FILTER_DEFAULTS = {
  search:         '',
  employee_id:    '',
  status:         '',
  priority:       '',
  deliveryMethod: '',
  site:           '',   // '' | 'pk' (Pritzwalk) | 'sb' (Tastomat)
  dateFrom:       '',
  dateTo:         ''
}

export default {
  name: 'OrdersView',
  components: { DataTable, Copyable, FilterDropdown, JdfUploadComponent, UserAvatar, OrderBoardView, OrderFlags, FarbigkeitSquare, StandortLogo },

  data() {
    return {
      orderService: null,
      _urlFilters:  null,

      orders: [],
      currentPage: 1,
      perPage: 25,
      totalPages: 1,
      totalCount: 0,
      // Default-Sortierung nach Lieferdatum (Chef 10.06.: beim Aufruf nach
      // Lieferdatum sortieren). Bleibt erhalten, bis der Nutzer eine Spalte wählt.
      sortField: 'delivery_date',
      sortDirection: 'asc',

      // Kopf-Reiter: Unfertige (Default) | ausgeliefert | Rechnungen | Alle.
      activeTab: 'unfertige',
      statusTabs: [
        { value: 'unfertige',   label: 'Unfertige Aufträge' },
        { value: 'ausgeliefert', label: 'Ausgelieferte Aufträge' },
        { value: 'rechnungen',  label: 'Rechnungen' },
        { value: 'alle',        label: 'Alle Aufträge' }
      ],

      filters: { ...FILTER_DEFAULTS },

      employeeList: [],

      showCustomerModal: false,
      selectedCustomer: null,
      loadingCustomer: false,
      customerContacts: [],
      loadingContacts: false,
      showJdfUpload: false,
      showArchived: false,
      viewMode: 'list',

      statusOptions:   ORDER_CONSTANTS.STATUS_OPTIONS,
      priorityOptions: ORDER_CONSTANTS.PRIORITY_OPTIONS,
      deliveryMethods: ORDER_CONSTANTS.DELIVERY_METHOD_OPTIONS,
      columns:         ORDER_CONSTANTS.TABLE_COLUMNS
    }
  },

  computed: {
    ...mapGetters(['loading', 'user', 'can']),
    isOwner() { return this.can('manage_users') },
    // Kopf-Reiter → Status-Umfang für den Backend-Filter (siehe orderService).
    tabStatusGroup() {
      return {
        unfertige:   'undelivered',
        ausgeliefert: 'delivered',
        rechnungen:  'invoiced',
        alle:        'all'
      }[this.activeTab] || 'undelivered'
    },
    activeFilterCount() {
      if (!this._urlFilters) return 0
      const { search, ...rest } = this.filters   // don't count the text search
      return this._urlFilters.activeCount(rest)
    },
    // #42 — Lieferdatum >14 Tage in der Vergangenheit, nicht ausgeliefert
    // TODO(#42): kept but unused — old-deadlines-bar template is commented out
    // until delivery dates are wired to real data.
    oldDeadlineOrders() {
      const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000
      const dead = new Set(['delivered', 'invoice_issued', 'archived'])
      return (this.orders || []).filter(o => {
        if (dead.has(o.status)) return false
        const d = this._parseDDMMYYYY(o.delivery_date)
        return d && d.getTime() < cutoff
      })
    },

    // Gruppierung innerhalb der aktuellen Seite (Besprechung 09.06.2026):
    // neue Aufträge (Auftrag erteilt) nach oben, dann Versand fertig, dann Rest.
    // Die Reihenfolge der Server-Sortierung (Lieferdatum) bleibt INNERHALB jeder
    // Gruppe erhalten — Array.sort ist in allen Ziel-Browsern stabil.
    displayOrders() {
      return [...(this.orders || [])].sort((a, b) => this._groupRank(a) - this._groupRank(b))
    },
    // id → CSS-Klasse(n): graue Zeile für neue Aufträge + 10px-Lücke an jeder
    // Gruppengrenze (erste Zeile der Folgegruppe).
    rowClassMap() {
      const map = {}
      let prevRank = null
      this.displayOrders.forEach(o => {
        const r = this._groupRank(o)
        const cls = []
        if (r === 0) cls.push('row-new')
        if (r === 1) cls.push('row-shipping')
        if (prevRank !== null && r !== prevRank) cls.push('row-grp-top')
        map[o.id] = cls.join(' ')
        prevRank = r
      })
      return map
    }
  },

  watch: {
    viewMode() { this.loadOrders() }
  },

  mounted() {
    this.orderService = new OrderService(this)
    this._urlFilters  = new UrlFilters(this.$router, this.$route, FILTER_DEFAULTS)
    Object.assign(this.filters, this._urlFilters.read())
    this.loadEmployees()
    this.loadOrders()
  },

  methods: {
    ...mapActions(['fetchData']),

    async loadEmployees() {
      try {
        const params = { include_recent_processors: true }
        if (this.showArchived) params.include_archived = true
        const response = await this.fetchData({ endpoint: 'users', params })
        this.employeeList = response?.data || []
      } catch (e) {
        console.error('Error loading employees:', e)
      }
    },

    toggleArchived() {
      this.showArchived = !this.showArchived
      this.loadEmployees()
    },

    async loadOrders() {
      try {
        const result = await this.orderService.loadOrders({
          currentPage:   this.viewMode === 'board' ? 1 : this.currentPage,
          perPage:       this.viewMode === 'board' ? 500 : this.perPage,
          sortField:     this.sortField,
          sortDirection: this.sortDirection,
          statusGroup:   this.tabStatusGroup,
          filters:       this.filters
        })
        this.orders      = result.orders
        this.currentPage = result.meta.currentPage
        this.totalPages  = result.meta.totalPages
        this.totalCount  = result.meta.totalCount
      } catch (e) {
        console.error('Error loading orders:', e)
      }
    },

    applyFilters() {
      this.currentPage = 1
      this._urlFilters.push(this.filters)
      this.loadOrders()
    },

    // Kopf-Reiter wechseln (Chef 10.06.). Setzt auf Seite 1 zurück und lädt neu.
    selectTab(tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      this.currentPage = 1
      this.loadOrders()
    },

    resetFilters() {
      Object.assign(this.filters, this._urlFilters.reset())
      this.currentPage = 1
      this.loadOrders()
    },

    // Click on an order's Ort badge → show only that site (Pritzwalk / Tastomat).
    // Clear it via the Ort dropdown or "Zurücksetzen".
    filterBySite(site) {
      if (!site || this.filters.site === site) return
      this.filters.site = site
      this.applyFilters()
    },

    // Click on an order's Versandart cell → filter to that shipping method.
    // delivery_method is an integer column (0–6); stored as a string so '0'
    // (DPD) survives the truthy check in buildOrderParams. Clear via the
    // Versandart dropdown or "Zurücksetzen".
    filterByDeliveryMethod(method) {
      if (method === null || method === undefined || method === '') return
      const val = String(method)
      if (this.filters.deliveryMethod === val) return
      this.filters.deliveryMethod = val
      this.applyFilters()
    },

    handleSearch(query) {
      this.filters.search = query
      this.applyFilters()
    },

    handleSort({ field, direction }) {
      this.sortField    = field
      this.sortDirection = direction
      this.loadOrders()
    },

    changePage(page) {
      this.currentPage = page
      this.loadOrders()
    },

    changePerPage(perPage) {
      this.perPage     = perPage
      this.currentPage = 1
      this.loadOrders()
    },

    getEmployee(id) {
      return this.employeeList.find(e => e.id === id || String(e.id) === String(id)) || null
    },

    viewOrder(id)  { this.$router.push(`/auftraege/${id}`) },
    editOrder(id)  { this.$router.push(`/auftraege/${id}/edit`) },

    async deleteOrder(id) {
      if (!confirm('Sind Sie sicher, dass Sie diesen Auftrag löschen möchten?')) return
      try {
        await this.orderService.deleteOrder(id)
        this.loadOrders()
      } catch (e) {
        console.error('Error deleting order:', e)
      }
    },

    handleJdfModalClose(result) {
      this.showJdfUpload = false
      if (result) this.loadOrders()
    },

    async showCustomerDetails(customerId) {
      this.showCustomerModal = true
      this.loadingCustomer   = true
      this.selectedCustomer  = null
      this.customerContacts  = []
      try {
        const result = await this.orderService.loadCustomerDetails(customerId)
        this.selectedCustomer = result.customer
        this.customerContacts = result.contacts
      } catch (e) {
        console.error('Error loading customer:', e)
      } finally {
        this.loadingCustomer = false
        this.loadingContacts = false
      }
    },

    closeCustomerModal() {
      this.showCustomerModal = false
      this.selectedCustomer  = null
      this.customerContacts  = []
    },

    // Formatters
    getPaymentStatusClass(s)  { return OrderFormatter.getPaymentStatusClass(s) },
    getPaymentStatusText(s)   { return OrderFormatter.getPaymentStatusText(s) },
    getPriorityClass(p)       { return OrderFormatter.getPriorityClass(p) },
    getPriorityText(p)        { return OrderFormatter.getPriorityText(p) },
    getStatusText(s)          { return OrderFormatter.getStatusText(s, this.statusOptions) },
    getStatusClass(s)         { return OrderFormatter.getStatusClass(s, this.statusOptions) },
    // Tint the whole Status cell with the status colour (instead of a badge).
    cellClass(column, item) {
      return column.key === 'status' ? `status-cell ${this.getStatusClass(item.status)}` : ''
    },
    // Gruppen-Rang für die Seiten-interne Sortierung: 0 = neu (Auftrag erteilt),
    // 1 = Versand fertig, 2 = alles andere.
    _groupRank(o) {
      if (o.status === 'order_received') return 0
      if (o.status === 'shipping_complete') return 1
      return 2
    },
    rowClass(item) {
      return this.rowClassMap[item.id] || ''
    },
    getMaterialStatusClass(s) { return OrderFormatter.getMaterialStatusClass(s) },
    getMaterialStatusText(s)  { return OrderFormatter.getMaterialStatusText(s) },
    formatNumber(n)           { return OrderFormatter.formatNumber(n) },
    formatDate(d)             { return OrderFormatter.formatDate(d) },
    formatDateDE(d)           { return OrderFormatter.formatDateDE(d) },
    formatTime(d)             { return OrderFormatter.formatTime(d) },
    formatSheetFormat(v)      { return OrderFormatter.formatSheetFormat(v) },

    formatDeliveryMethod(method) {
      return { '0': 'DPD', '1': 'Spedition', '2': 'Selbstabholer', '3': 'Kurier', '4': 'Post', '5': 'UPS' }[String(method)] || method
    },

    // Parses the date strings returned by the orders endpoint. Today it's
    // "YYYY-MM-DD [HH:MM]" via OrdersController#format_date, but accept the
    // legacy "DD/MM/YYYY" and "DD.MM.YYYY" too for safety.
    _parseDDMMYYYY(s) {
      if (!s) return null
      const str = String(s).trim()
      let m
      if ((m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/))) {
        const d = new Date(+m[1], +m[2] - 1, +m[3])
        return isNaN(d.getTime()) ? null : d
      }
      if ((m = str.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/))) {
        const d = new Date(+m[3], +m[2] - 1, +m[1])
        return isNaN(d.getTime()) ? null : d
      }
      return null
    }
  }
}
</script>

<style scoped>
@import '../styles/orderStyles.scss';

.view-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
.view-header h2 {
  margin: 0;
  flex-shrink: 0;
}

.header-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.archived-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.archived-toggle-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.archived-toggle-btn.is-active {
  background: #ddf4ff;
  border-color: #0969da;
  color: #0550ae;
}
.archived-toggle-btn.is-active i {
  color: #0969da;
}
.archived-toggle-btn i {
  font-size: 0.85rem;
  color: #6b7280;
}

.view-toggle {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.view-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  font-size: 0.82rem;
}

.view-toggle-btn + .view-toggle-btn {
  border-left: 1px solid #d1d5db;
}

.view-toggle-btn:hover {
  background: #f9fafb;
  color: #374151;
}

.view-toggle-btn.is-active {
  background: #ddf4ff;
  color: #0550ae;
}

.import-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  white-space: nowrap;
}
.import-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
}
.import-btn i {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Kopf-Reiter (Chef 10.06.) */
.status-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.status-tab {
  padding: 7px 16px;
  font-size: 0.86rem;
  font-weight: 600;
  color: #57606a;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;
}
.status-tab:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}
.status-tab.is-active {
  background: #0969da;
  border-color: #0969da;
  color: #fff;
}

.filter-bar {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 10px;
  padding: 10px 14px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Date range */
.date-range {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.date-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #57606a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 4px;
}

.date-input {
  padding: 5px 8px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 0.82rem;
  color: #24292f;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  width: 130px;
}

.date-input:focus {
  border-color: #0969da;
  box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.12);
}

.date-sep {
  color: #57606a;
  font-size: 0.85rem;
}

/* Clear button */
.clear-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: transparent;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 0.82rem;
  color: #57606a;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;
}

.clear-filters-btn:hover {
  background: #fff1f0;
  border-color: #cf222e;
  color: #cf222e;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #57606a;
  color: #fff;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
}

.clear-filters-btn:hover .filter-count {
  background: #cf222e;
}

/* Employee avatar cell */
.emp-avatar-cell {
  position: relative;
  display: inline-block;
}

.emp-avatar-link {
  display: block;
  line-height: 0;
  border-radius: 50%;
  transition: opacity 0.15s;
}
.emp-avatar-link:hover {
  opacity: 0.85;
}

.emp-tooltip {
  display: none;
  position: absolute;
  top: 50%;
  left: calc(100% + 8px);
  transform: translateY(-50%);
  z-index: 200;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  padding: 6px 10px 6px 8px;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  pointer-events: none;
  font-size: 0.85rem;
  font-weight: 500;
  color: #24292f;
}

.emp-avatar-cell:hover .emp-tooltip {
  display: flex;
}

/* Order number cell: number + flag cluster on one line */
.order-number-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

/* Ort column: click the logo to filter by site (Pritzwalk / Tastomat). */
.standort-filter-btn {
  border: none;
  background: transparent;
  padding: 2px 4px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: background 0.12s;
}
.standort-filter-btn:hover { background: #eef2ff; }
.standort-filter-btn.active { background: #dbeafe; box-shadow: inset 0 0 0 1px #93c5fd; }

/* Versandart column: click the value to filter by shipping method. */
.cell-filter-btn {
  border: none;
  background: transparent;
  padding: 2px 6px;
  border-radius: 6px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  white-space: nowrap;
  transition: background 0.12s;
}
.cell-filter-btn:hover { background: #eef2ff; }
.cell-filter-btn.active { background: #dbeafe; box-shadow: inset 0 0 0 1px #93c5fd; }

/* Boss-Mail 2: zweizeilige Zellen (Lieferdatum+Uhrzeit, Druckformat+Maschine) */
.stacked-cell { display: flex; flex-direction: column; align-items: center; line-height: 1.15; }
.stacked-cell .sub-line { color: #6c757d; font-size: 0.78em; }
</style>
